import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, RefreshCw, Repeat, Bell, Lock,
} from 'lucide-react';
import { Button } from './ui/button';

const ROWS = [
  { name: 'AK-47 | Redline', cond: 'FT', buy: 11.20, now: 14.60, pct: '+30.4%', up: true },
  { name: 'AWP | Asiimov', cond: 'BS', buy: 62.00, now: 70.15, pct: '+13.1%', up: true },
  { name: 'Karambit | Doppler', cond: 'FN', buy: 410.00, now: 403.80, pct: '-1.5%', up: false },
  { name: 'M4A4 | Howl', cond: 'MW', buy: 1840.00, now: 2015.00, pct: '+9.5%', up: true },
];

const FEATURES = [
  {
    icon: RefreshCw,
    title: 'Live price data',
    desc: 'Every item is priced against live marketplace data, refreshed continuously, so the value you see is the value right now, not last week.',
  },
  {
    icon: ArrowUpRight,
    title: 'Steam inventory sync',
    desc: 'Pull your Steam inventory in one click. New items get flagged automatically so nothing slips past your tracker.',
  },
  {
    icon: Repeat,
    title: 'Trades, not just sales',
    desc: 'Record item-for-item trades alongside cash sales. Profit math accounts for both, so your totals never drift from reality.',
  },
  {
    icon: Bell,
    title: 'Price targets & alerts',
    desc: 'Set a target on anything you hold and get notified the moment the market reaches it, no more checking prices by hand.',
  },
  {
    icon: Lock,
    title: 'Your data stays yours',
    desc: 'Inventory and trade history are scoped to your account only. Nothing is shared, sold, or shown to anyone else.',
  },
];

function Divider() {
  return <div className="h-px w-full bg-gradient-to-r from-transparent via-[var(--green-border)] to-transparent" />;
}

function NavBar() {
  return (
    <header className="sticky top-0 z-50 bg-[rgba(12,14,22,0.85)] backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
        <div className="flex items-center gap-2.5">
          <img src="/logo-pt-white.png" alt="CS2 Profit Tracker" className="h-8 w-8 object-contain" />
          <span className="text-[1.04rem] font-bold tracking-tight text-white">CS2 Profit Tracker</span>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="sm">
            <Link to="/signin">Sign in</Link>
          </Button>
          <Button asChild size="sm">
            <Link to="/signin">Get started</Link>
          </Button>
        </div>
      </div>
      <Divider />
    </header>
  );
}

function MockTrackerTable() {
  return (
    <div className="relative w-full max-w-[480px] [perspective:1600px]">
      {/* stacked ghost layers behind, for depth */}
      <div
        className="absolute inset-0 rounded-2xl border border-white/[0.05] bg-[var(--panel)]"
        style={{ transform: 'translate3d(18px, 26px, -80px) rotateY(-10deg) rotateX(4deg)' }}
      />
      <div
        className="absolute inset-0 rounded-2xl border border-white/[0.07] bg-[var(--panel-2)]"
        style={{ transform: 'translate3d(9px, 13px, -40px) rotateY(-10deg) rotateX(4deg)' }}
      />

      {/* main card */}
      <div
        className="relative overflow-hidden rounded-2xl border border-[var(--green-border)]/40 bg-[var(--panel)] shadow-[0_40px_90px_-25px_rgba(0,0,0,0.95)]"
        style={{ transform: 'rotateY(-10deg) rotateX(4deg)', transformStyle: 'preserve-3d' }}
      >
        {/* top sheen for a glass-like 3D highlight */}
        <div
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 35%)' }}
        />

        <div className="relative flex items-center justify-between border-b border-white/[0.06] px-5 py-3.5">
          <div>
            <div className="text-[0.91rem] font-semibold uppercase tracking-wider text-[var(--text-dim)]">
              Portfolio overview
            </div>
            <div className="mt-1 text-[1.45rem] font-bold tracking-tight text-white">
              $6,940.10
            </div>
          </div>
          <div className="text-right">
            <div className="text-[0.91rem] font-semibold uppercase tracking-wider text-[var(--text-dim)]">
              Realized profit
            </div>
            <div className="mt-1 text-[1.13rem] font-bold text-[var(--green)]">
              +$1,284.60
            </div>
          </div>
        </div>
        <div className="relative">
          {ROWS.map((r, i) => (
            <div
              key={r.name}
              className={`flex items-center justify-between px-5 py-3 ${i !== ROWS.length - 1 ? 'border-b border-white/[0.04]' : ''}`}
            >
              <div className="min-w-0">
                <div className="truncate text-[0.98rem] font-semibold text-white">{r.name}</div>
                <div className="text-[0.9rem] text-[var(--text-muted)]">
                  {r.cond} · bought ${r.buy.toFixed(2)}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <span className="text-[0.98rem] font-semibold text-white">${r.now.toFixed(2)}</span>
                <span
                  className={`w-[60px] shrink-0 rounded-md px-1.5 py-0.5 text-right text-[0.91rem] font-bold ${
                    r.up ? 'bg-[var(--green-bg)] text-[var(--green)]' : 'bg-[var(--red-bg)] text-[var(--red)]'
                  }`}
                >
                  {r.pct}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function HomePage() {
  return (
    <div className="min-h-screen w-full overflow-hidden bg-[var(--bg)] text-[var(--text)]">
      <NavBar />

      {/* Hero */}
      <section className="relative mx-auto max-w-6xl px-6 pb-24 pt-16 sm:pt-24">
        <div
          className="pointer-events-none absolute -right-40 -top-20 h-[420px] w-[420px] rounded-full opacity-[0.10] blur-3xl"
          style={{ background: 'radial-gradient(circle, var(--green) 0%, transparent 70%)' }}
        />
        <div className="grid items-center gap-16 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="relative">
            <h1 className="max-w-xl text-[2.5rem] font-extrabold leading-[1.08] tracking-tight text-white sm:text-[3.1rem]">
              Every skin you buy, sell,<br />
              and trade, in <span className="text-[var(--green)]">one ledger</span>.
            </h1>
            <p className="mt-6 max-w-md text-[1.04rem] leading-relaxed text-[var(--text-muted)]">
              Stop reconstructing your CS2 profit from Steam history and spreadsheets.
              Log trades as they happen, sync your inventory, and see real profit and
              loss update automatically.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Button asChild size="lg">
                <Link to="/signin">
                  Get started free
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <a href="#features">See what it tracks</a>
              </Button>
            </div>
          </div>

          <div className="flex justify-center lg:justify-end">
            <MockTrackerTable />
          </div>
        </div>
      </section>

      <Divider />

      {/* Features: asymmetric list, not a card grid */}
      <section id="features" className="mx-auto max-w-6xl px-6 py-20">
        <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <h2 className="text-[1.72rem] font-bold leading-tight tracking-tight text-white">
              Built around how CS2 traders actually work.
            </h2>
            <p className="mt-4 max-w-sm text-[0.98rem] leading-relaxed text-[var(--text-muted)]">
              Not a spreadsheet template, but a tracker that understands trades, fees,
              conditions, and float, because that's what actually moves your P&amp;L.
            </p>
          </div>

          <div>
            {FEATURES.map(({ icon: Icon, title, desc }, i) => (
              <div
                key={title}
                className={`flex gap-4 py-5 ${i !== FEATURES.length - 1 ? 'border-b border-white/[0.06]' : ''}`}
              >
                <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg border border-[var(--green-border)]/50 bg-[var(--green-bg)]">
                  <Icon className="size-4 text-[var(--green)]" />
                </div>
                <div>
                  <h3 className="text-[1.01rem] font-semibold text-white">{title}</h3>
                  <p className="mt-1.5 text-[0.98rem] leading-relaxed text-[var(--text-muted)]">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      {/* How it works: connected timeline, not stacked cards */}
      <section className="mx-auto max-w-6xl px-6 py-20">
        <h2 className="mb-14 text-[1.72rem] font-bold tracking-tight text-white">
          Set up in three steps.
        </h2>
        <div className="grid gap-10 sm:grid-cols-3">
          {[
            ['01', 'Create your account', 'No Steam login or API key needed to sign up, just a username and password.'],
            ['02', 'Add or import items', 'Log purchases by hand, or sync your Steam inventory to pull everything in at once.'],
            ['03', 'Track profit as you go', 'Mark items sold or traded and your overview, analytics, and history update instantly.'],
          ].map(([n, title, desc], i, arr) => (
            <div key={n} className="relative">
              <div className="flex items-center gap-3">
                <span className="text-[1.09rem] font-bold text-[var(--green)]">{n}</span>
                {i !== arr.length - 1 && (
                  <span className="hidden h-px flex-1 bg-gradient-to-r from-[var(--green-border)] to-transparent sm:block" />
                )}
              </div>
              <h3 className="mt-3 text-[1.03rem] font-semibold text-white">{title}</h3>
              <p className="mt-2 text-[0.97rem] leading-relaxed text-[var(--text-muted)]">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <Divider />

      {/* CTA */}
      <section className="mx-auto max-w-6xl px-6 py-20">
        <div className="flex flex-col items-start justify-between gap-8 rounded-2xl border border-[var(--green-border)]/40 bg-[var(--panel)] px-8 py-10 sm:flex-row sm:items-center">
          <div>
            <h2 className="text-[1.45rem] font-bold tracking-tight text-white">
              Start tracking your profit today.
            </h2>
            <p className="mt-2 text-[0.98rem] text-[var(--text-muted)]">
              Free to use. Import your inventory and log your first trade in a couple of minutes.
            </p>
          </div>
          <Button asChild size="lg" className="shrink-0">
            <Link to="/signin">
              Get started free
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </section>

      <Divider />

      {/* Footer */}
      <footer>
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-6 py-8 sm:flex-row">
          <div className="flex items-center gap-2">
            <img src="/logo-pt-white.png" alt="CS2 Profit Tracker" className="h-6 w-6 object-contain" />
            <span className="text-[0.97rem] font-semibold text-white">CS2 Profit Tracker</span>
          </div>
          <p className="text-[0.93rem] text-[var(--text-dim)]">
            Not affiliated with Valve or Steam. CS2 is a trademark of Valve Corporation.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default HomePage;
